import type { Messages } from './messages';

export const it: Messages = {
  'lang.name': 'Italiano',
  'lang.aria': 'Lingua',
  'app.title': 'Sintetizzatore web',

  // Header — mapping shift
  'header.shift.aria': 'Sposta la mappatura della tastiera',
  'shift.octDown': '− 8ve',
  'shift.noteDown': '− nota',
  'shift.noteUp': '+ nota',
  'shift.octUp': '+ 8ve',
  'shift.octDown.title': 'Un’ottava più in basso (tasto −)',
  'shift.noteDown.title': 'Un tasto bianco più in basso (tasto _)',
  'shift.noteUp.title': 'Un tasto bianco più in alto (tasto +)',
  'shift.octUp.title': 'Un’ottava più in alto (tasto =)',

  // Install button
  'install.prompt': 'Installa l’app',
  'install.installed': 'Installata',
  'install.promptTitle': 'Installa il sintetizzatore come app',
  'install.uninstallHint':
    'L’app è installata. I browser non permettono di disinstallarla da una pagina; usa il menu del browser (⋮) → "Disinstalla", oppure le app di sistema.',
  'install.manualHint':
    'Se non compare automaticamente: dal menu del browser (⋮) scegli "Installa app". Su iPhone/iPad: Condividi → "Aggiungi alla schermata Home".',

  // Metronome
  'metronome.label': 'Metronomo',
  'metronome.tempoDown.aria': 'Tempo più lento',
  'metronome.tempoUp.aria': 'Tempo più veloce',
  'metronome.bpm.aria': 'Tempo (BPM)',
  'metronome.tap': 'Tap',
  'metronome.tap.aria': 'Batti il tempo con un clic',
  'metronome.start': 'Avvia',
  'metronome.stop': 'Ferma',
  'meter.aria': 'Ritmo (indicazione di tempo)',
  'meter.minus.aria': 'Meno pulsazioni',
  'meter.plus.aria': 'Più pulsazioni',
  'meter.accents.aria': 'Accenti sulle pulsazioni',
  'meter.custom': 'Personalizzato…',

  'sustain': 'Sustain',
  'reset.all': 'Reimposta tutte le impostazioni',

  // Section titles
  'section.keyboard': 'Tastiera',
  'section.engine': 'Motore audio',
  'section.sound': 'Strumento e suono',
  'section.effects': 'Effetti',
  'section.curve': 'Curva di livello per registro (bassi ↔ alti)',
  'section.offline': 'Disponibilità offline',

  // Keyboard section
  'labels.aria': 'Etichette sui tasti',
  'label.cde': 'CDE',
  'label.cde.title': 'Lettere sui tasti (C D E …)',
  'label.doremi': 'DoReMi',
  'label.doremi.title': 'Solfeggio sui tasti (Do Re Mi …)',
  'label.keys': 'Tasti',
  'label.keys.title': 'Il tasto della tastiera, su ogni nota',
  'label.octave': 'Ottava',
  'label.octave.title': 'Il numero dell’ottava, in alto sul tasto',
  'field.startNote': 'Nota iniziale',
  'field.startOctave': 'Ottava iniziale',
  'field.octaveCount': 'Numero di ottave',
  'field.displayMode': 'Modalità di visualizzazione',
  'display.scroll': 'Scorrimento orizzontale',
  'display.fit1': 'Adattata su una riga',
  'display.fit2': 'Adattata su due righe',
  'range.label': 'Estensione della tastiera',
  'range.keys': '{n} tasti',
  'range.low.aria': 'La nota più grave',
  'range.high.aria': 'La nota più acuta',
  'range.drag.aria': "Sposta l'intero intervallo",
  'octaveCount.full': 'Estensione completa (88 tasti)',
  'octaveCount.partial': '— (estensione parziale)',

  // Engine section
  'field.engine': 'Motore',
  'engine.synth': 'Sintetizzato (Tone.js)',
  'engine.sampler': 'Sample reale (strumenti registrati)',
  'field.synthType': 'Tipo di sintetizzatore',
  'field.preset': 'Preset',
  'preset.aria': 'Preset strumento',
  'field.waveform': 'Timbro',
  'waveform.aria': 'Forma d’onda (timbro)',
  'field.instrument': 'Strumento',
  'field.density': 'Densità dei sample',
  'density.6': 'Veloce (1 sample / 6 semitoni)',
  'density.3': 'Bilanciato (1 sample / 3 semitoni)',
  'density.1': 'Completo (tutti i sample)',

  // Sampler status
  'status.loading': 'Caricamento di "{instrument}": {loaded} / {total} sample…',
  'status.upgrading': 'Riproducibile ora ({count} sample) · caricamento della qualità completa…',
  'status.ready': 'Pronto: {count} sample ({low}–{high}){failed}. Le note intermedie vengono trasposte.',
  'status.readyFailed': ' · {failed} non riusciti',
  'status.cached': 'Strumento caricato (dalla cache).',
  'status.errNoSamples': 'Lo strumento non ha sample nella libreria.',
  'status.errLoadFailed': 'Impossibile caricare alcun sample (controlla la connessione).',

  // Keyboard loading overlay
  'overlay.loading': 'Caricamento di "{instrument}"… {loaded} / {total}',
  'overlay.preparing': 'Preparazione dello strumento…',
  'overlay.error': 'Impossibile caricare i sample: {message}',

  // Sound knobs
  'knob.attack': 'Attacco (s)',
  'knob.release': 'Rilascio (s)',
  'knob.volume': 'Volume',
  'knob.filter': 'Luminosità',
  'knob.filter.aria': 'Luminosità (filtro passa-basso)',
  'knob.echo': 'Echo',
  'knob.reverb': 'Reverb',
  'resetInstrument': 'Reimposta ai valori predefiniti',
  'resetInstrument.title': 'Riporta il suono di questo strumento ai valori predefiniti',

  // Filter tone words
  'filter.veryDark': 'molto scuro',
  'filter.dark': 'scuro',
  'filter.neutral': 'neutro',
  'filter.bright': 'brillante',
  'filter.veryBright': 'molto brillante',

  // Register curve
  'curve.hint':
    'Compensazione della sonorità percepita per gli oscillatori sintetizzati, che sono tecnicamente piatti ma suonano poveri di bassi. Gli strumenti registrati hanno già il proprio equilibrio (misurato piatto entro ~2,7 dB da C1 a C7), perciò per impostazione predefinita la curva non viene applicata a loro.',
  'curve.bassPre': 'Bassi:',
  'curve.bassPost': 'dB / ottava sotto Do4',
  'curve.treblePre': 'Alti:',
  'curve.treblePost': 'dB / ottava sopra Do4',
  'curve.onSamples': 'Applica anche ai sample reali',
  'curve.notApplied': 'La curva non viene applicata al motore dei sample (spunta la casella per applicarla).',

  // Offline section
  'offline.hint':
    'Installa l’app dal menu del browser ("Installa" / "Aggiungi alla schermata Home"), poi scarica in cache tutti i sample reali. Dopodiché il sintetizzatore funziona completamente offline, su qualsiasi dispositivo. Il download è dell’ordine di ~100–250 MB e avviene una sola volta.',
  'offline.download': 'Scarica tutto per l’offline',
  'offline.refresh': 'Aggiorna la cache offline',
  'offline.done': 'Tutti i sample sono stati scaricati — disponibili offline.',
  'offline.downloading': 'Download di {done} / {total} sample…{failed}',
  'offline.failedSuffix': ' ({failed} non riusciti)',
  'offline.doneAll': 'Fatto — tutti i {total} sample sono disponibili offline.',
  'offline.doneWithErrors': 'Scaricato con {failed} errori — riprova per completare la cache.',

  // Engine notices
  'engine.fallback': 'Impossibile creare "{label}" ({reason}). Si è tornati a Synth.',
  'worklet.hint':
    'La pagina è aperta direttamente da disco (file://), dove il browser rifiuta i moduli AudioWorklet: PluckSynth e BitCrusher non possono funzionare. Esegui "npm run dev" e apri l’indirizzo http mostrato lì per farli funzionare.',

  // Keyboard area
  'keyboard.aria': 'Tastiera',
  'keyboard.resize.aria': 'Regola l’altezza della tastiera (trascina, o frecce su/giù; doppio clic = automatico)',
  'keyboard.resize.title': 'Trascina per l’altezza · doppio clic = automatico',

  // Presets
  'preset.piano': 'Pianoforte',
  'preset.organ': 'Organo',
  'preset.strings': 'Archi',
  'preset.bass': 'Basso',
  'preset.lead': 'Lead',
  'preset.custom': 'Personalizzato',

  // Synth types
  'synth.synth': 'Synth',
  'synth.monosynth': 'MonoSynth',
  'synth.amsynth': 'AMSynth',
  'synth.fmsynth': 'FMSynth',
  'synth.duosynth': 'DuoSynth',
  'synth.membrane': 'MembraneSynth',
  'synth.metal': 'MetalSynth',
  'synth.noise': 'NoiseSynth (monofonico)',
  'synth.pluck': 'PluckSynth (monofonico)',

  // Waveforms
  'waveform.sine': 'Sinusoidale',
  'waveform.triangle': 'Triangolare',
  'waveform.sawtooth': 'Dente di sega',
  'waveform.square': 'Quadra',

  // Effects
  'effect.mix': 'Mix',
  'effect.chorus': 'Chorus',
  'effect.distortion': 'Distorsione',
  'effect.bitcrusher': 'BitCrusher',
  'effect.vibrato': 'Vibrato',
  'effect.autowah': 'AutoWah',
  'effect.phaser': 'Phaser',
  'effect.pingpong': 'Ping-pong delay',
  'effect.stereowidener': 'Stereo widener',
  'effect.eq3': 'EQ a 3 bande',
  'effect.pitchshift': 'Pitch shift',

  // Parameters (shared by effects and synths, keyed by param key)
  'param.frequency': 'Frequenza (Hz)',
  'param.distortion': 'Quantità',
  'param.bits': 'Bit',
  'param.depth': 'Profondità',
  'param.octaves': 'Ottave',
  'param.delayTime': 'Tempo (s)',
  'param.width': 'Larghezza',
  'param.low': 'Bassi (dB)',
  'param.mid': 'Medi (dB)',
  'param.high': 'Alti (dB)',
  'param.pitch': 'Semitoni',
  'param.harmonicity': 'Armonicità',
  'param.modulationIndex': 'Indice di modulazione',
  'param.vibratoAmount': 'Vibrato',
  'param.pitchDecay': 'Decadimento tono (s)',
  'param.dampening': 'Smorzamento (Hz)',
  'param.resonance': 'Risonanza',
  'param.type': 'Colore rumore',

  // Instruments
  'instrument.piano': 'Pianoforte',
  'instrument.guitar-acoustic': 'Chitarra acustica',
  'instrument.guitar-nylon': 'Chitarra con corde in nylon',
  'instrument.guitar-electric': 'Chitarra elettrica',
  'instrument.bass-electric': 'Basso elettrico',
  'instrument.bassoon': 'Fagotto',
  'instrument.cello': 'Violoncello',
  'instrument.clarinet': 'Clarinetto',
  'instrument.contrabass': 'Contrabbasso',
  'instrument.flute': 'Flauto',
  'instrument.french-horn': 'Corno francese',
  'instrument.harmonium': 'Harmonium',
  'instrument.harp': 'Arpa',
  'instrument.organ': 'Organo',
  'instrument.saxophone': 'Sassofono',
  'instrument.trombone': 'Trombone',
  'instrument.trumpet': 'Tromba',
  'instrument.tuba': 'Tuba',
  'instrument.violin': 'Violino',
  'instrument.xylophone': 'Xilofono',

  // Meters
  'meter.4/4': '4/4 (comune)',
  'meter.3/4': '3/4 (valzer)',
  'meter.2/4': '2/4',
  'meter.6/8': '6/8 (3+3)',
  'meter.5/4': '5/4 (Take Five · 3+2)',
  'meter.7/8': '7/8 (2+2+3)',
  'meter.9/8': '9/8 (Balcanico · 2+2+2+3)',
};
